<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { computePosition, flip, shift, offset, arrow } from '@floating-ui/dom'

const props = defineProps({
  simpleText: {
    type: String,
    default: ''
  },
  // Content can be an array for click-triggered tooltips, or an object for hover-triggered ones
  content: {
    type: [Array, Object],
    default: () => []
  },
  // Controls visibility for click-triggered tooltips
  isActive: {
    type: Boolean,
    default: false
  },
  // Determines if the tooltip is triggered by click or hover
  triggerOnClick: {
    type: Boolean,
    default: false
  },
  // To show the arrow, useful for non-event tooltips
  showArrow: {
    type: Boolean,
    default: false
  },
  employer: {
    type: Object,
    default: () => ({})
  },
  source: {
    type: String,
    default: ''
  },
  contentClass: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  placement: {
    type: String,
    default: 'top'
  }
})

const emit = defineEmits(['tooltipOpen', 'event-clicked'])

const tooltipRef = ref(null)
const triggerRef = ref(null)
const arrowRef = ref(null)
const isVisible = ref(false)
let hideTimer = null
let showTimer = null

const updatePosition = async () => {
  if (!triggerRef.value || !tooltipRef.value) return
  const { x, y, placement: actualPlacement, middlewareData } = await computePosition(
    triggerRef.value,
    tooltipRef.value,
    {
      placement: props.placement,
      middleware: [offset(8), flip(), shift({ padding: 5 }), arrow({ element: arrowRef.value })]
    }
  )
  Object.assign(tooltipRef.value.style, {
    left: `${x}px`,
    top: `${y}px`
  })
  if (middlewareData.arrow && arrowRef.value) {
    const { x: arrowX, y: arrowY } = middlewareData.arrow
    const side = actualPlacement.split('-')[0]
    const staticSide = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' }[side]

    // Reset all arrow styles first
    Object.assign(arrowRef.value.style, {
      left: '', right: '', top: '', bottom: '',
      borderTop: '0', borderBottom: '0', borderLeft: '0', borderRight: '0'
    })

    if (side === 'top' || side === 'bottom') {
      Object.assign(arrowRef.value.style, {
        left: arrowX != null ? `${arrowX}px` : '',
        [staticSide]: '-6px',
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        [side === 'top' ? 'borderTop' : 'borderBottom']: '6px solid #000'
      })
    } else {
      Object.assign(arrowRef.value.style, {
        top: arrowY != null ? `${arrowY}px` : '',
        [staticSide]: '-12px',
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
        [side === 'left' ? 'borderLeft' : 'borderRight']: '6px solid #000'
      })
    }
  }
}

// --- Logic for hover-based tooltips ---
const showTooltip = () => {
  if (props.disabled || props.triggerOnClick) return
  if (hideTimer) clearTimeout(hideTimer)
  if (isVisible.value) return
  if (showTimer) clearTimeout(showTimer)
  showTimer = setTimeout(() => {
    isVisible.value = true
    nextTick(updatePosition)
  }, 150)
}

const hideTooltip = () => {
  if (props.triggerOnClick) return
  if (showTimer) clearTimeout(showTimer)
  hideTimer = setTimeout(() => {
    isVisible.value = false
  }, 80)
}

// --- Logic for click-based tooltips ---
const handleTriggerClick = () => {
  if (props.disabled || !props.triggerOnClick) return
  if (!props.isActive) {
    emit('tooltipOpen', true) // Signal parent to make this tooltip active
  }
}

const handleCloseClick = () => {
  emit('tooltipOpen', null) // Signal parent to deactivate this tooltip
}

watch(
  () => props.isActive,
  (newValue) => {
    if (props.triggerOnClick) {
      isVisible.value = newValue
      if (newValue) {
        nextTick(updatePosition)
      }
    }
  }
)

// --- Common Logic ---
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  })
}

const onClickEvent = (event) => {
  emit('event-clicked', { event, employer: props.employer, source: props.source })
  if (props.triggerOnClick) {
    handleCloseClick()
  }
}

const handleClickOutside = (event) => {
  if (
    tooltipRef.value &&
    !tooltipRef.value.contains(event.target) &&
    triggerRef.value &&
    !triggerRef.value.contains(event.target) &&
    isVisible.value
  ) {
    handleCloseClick()
  }
}

const handleEscKey = (event) => {
  if (event.key === 'Escape' && isVisible.value) {
    handleCloseClick()
  }
}

onMounted(() => {
  window.addEventListener('scroll', updatePosition, true)
  window.addEventListener('resize', updatePosition, true)
  window.addEventListener('keydown', handleEscKey)
})

watch(() => props.isActive, (newValue) => {
  if (newValue) {
    // Use timeout to avoid immediate closing on the same click that opens the tooltip
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true)
    }, 0)
  } else {
    document.removeEventListener('click', handleClickOutside, true)
  }
})

onUnmounted(() => {
  window.removeEventListener('scroll', updatePosition, true)
  window.removeEventListener('resize', updatePosition, true)
  window.removeEventListener('keydown', handleEscKey)
  document.removeEventListener('click', handleClickOutside, true)
})
</script>

<template>
  <div
    ref="triggerRef"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    @click="handleTriggerClick"
    class="tooltip-trigger"
    data-bcs-tooltip-trigger="true"
  >
    <slot></slot>
    <Teleport to="body" v-if="isVisible">
      <div ref="tooltipRef" class="tooltip" role="tooltip">
      <div v-if="triggerOnClick" @click.stop="handleCloseClick" class="tooltip-close d-flex justify-content-end">
        <i class="bi bi-x"></i>
      </div>
      <div :class="contentClass || 'tooltip-content'">
        <div v-if="simpleText" v-html="simpleText"></div>
        <template v-else>
          <div v-for="dateGroup in content" :key="dateGroup.date" class="date-group">
            <div class="tooltip-header">Due {{ formatDate(dateGroup.date) }}</div>
            <div class="tooltip-body">
              <div v-for="event in dateGroup.events" :key="event.id" class="reminder-item">
                <div @click.stop="onClickEvent(event)" class="reminder-type">
                  {{ event.name }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
        <div v-if="showArrow" ref="arrowRef" class="tooltip-arrow"></div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.tooltip-trigger {
  display: inline-block;
  position: relative;
}

.simple-text-tooltip {
  position: relative;
  padding: 0.5rem 0.6rem;
}

.tooltip {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 3000;
  pointer-events: auto;
  width: max-content;
  max-width: 300px;
  min-width: min-content;
  visibility: visible;
  opacity: 1;
  background: #000;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  color: white;
  font-size: 14px;

  .tooltip-content {
    position: relative;
    padding: 0 16px 12px 16px;
  }

  .date-group:not(:last-child) {
    margin-bottom: 12px;
  }

  .tooltip-header {
    font-weight: 600;
    padding-top: 8px;
  }

  .tooltip-body {
    max-height: 300px;
    overflow-y: auto;
  }

  .reminder-type {
    color: white;
    text-decoration: underline;
    cursor: pointer;
    font-size: 14px;
    line-height: 1.4;
  }
}

.tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #000;
  bottom: -6px;
}

.tooltip-close {
  cursor: pointer;
  padding: 4px 4px 0 0;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;

  i {
    font-size: 16px;
  }
}
.tooltip-item-row {
  white-space: nowrap;
  padding: 0.2rem 0.4rem;
}
.reminder-type {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.reminder-date {
  flex: 0 0 auto;
  white-space: nowrap;
}
.assessment-steps {
  color: white;
  font-size: 16px;
  line-height: 1.4;
  position: relative;
  padding: 5px;
}
</style>